import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';

export interface InvoiceFormData {
  contractor: {
    name: string;
    email: string;
    address: string;
    taxId: string;
  };
  client: {
    name: string;
    address: string;
  };
  meta: {
    number: string;
    date: string;
  };
  notes: string;
  invoiceMonth: string;
}

interface InvoiceFormProps {
  data: InvoiceFormData;
  onChange: (field: string, value: string) => void;
}

export const InvoiceForm: React.FC<InvoiceFormProps> = ({ data, onChange }) => {
  const { t } = useTranslation('invoices');

  const handleNestedChange = (section: string, field: string, value: string) => {
    onChange(`${section}.${field}`, value);
  };

  return (
    <div className="space-y-6">
      {/* Contractor Information */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4">{t('generator.form.contractorInformation')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2 col-span-2">
            <Label htmlFor="contractor-name">{t('generator.form.name')}</Label>
            <Input
              id="contractor-name"
              value={data.contractor.name}
              onChange={(e) => handleNestedChange('contractor', 'name', e.target.value)}
              placeholder={t('generator.form.namePlaceholder')}
            />
            <div
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
              role="alert"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t('generator.form.vendorNameWarning')}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="contractor-email">{t('generator.form.email')}</Label>
            <Input
              id="contractor-email"
              type="email"
              value={data.contractor.email}
              onChange={(e) => handleNestedChange('contractor', 'email', e.target.value)}
              placeholder={t('generator.form.emailPlaceholder')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contractor-taxId">{t('generator.form.taxId')}</Label>
            <Input
              id="contractor-taxId"
              value={data.contractor.taxId}
              onChange={(e) => handleNestedChange('contractor', 'taxId', e.target.value)}
              placeholder={t('generator.form.taxIdPlaceholder')}
            />
          </div>
          <div className="space-y-2 col-span-2">
            <Label htmlFor="contractor-address">{t('generator.form.address')}</Label>
            <Textarea
              id="contractor-address"
              value={data.contractor.address}
              onChange={(e) => handleNestedChange('contractor', 'address', e.target.value)}
              placeholder={t('generator.form.addressPlaceholder')}
              rows={2}
            />
          </div>
        </div>
      </Card>

      {/* Client Information - Hidden since it never changes */}
      <Card className="p-4" style={{ display: 'none' }}>
        <h3 className="text-lg font-semibold mb-4">Bill To</h3>
        <div className="space-y-1 text-sm">
          <p className="font-semibold">Prosigliere</p>
          <p>16517 Along Creek Cove</p>
          <p>Austin, TX 78717</p>
        </div>
      </Card>

      {/* Invoice Metadata */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4">{t('generator.form.invoiceDetails')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="invoice-number">{t('generator.form.invoiceNumber')}</Label>
            <Input
              id="invoice-number"
              value={data.meta.number}
              onChange={(e) => handleNestedChange('meta', 'number', e.target.value)}
              placeholder="INV-001"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invoice-month">{t('generator.form.invoiceMonth')}</Label>
            <Input
              id="invoice-month"
              type="month"
              value={data.invoiceMonth}
              onChange={(e) => onChange('invoiceMonth', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invoice-date">{t('generator.form.invoiceDate')}</Label>
            <Input
              id="invoice-date"
              type="date"
              value={data.meta.date}
              onChange={(e) => handleNestedChange('meta', 'date', e.target.value)}
            />
          </div>
          <div className="space-y-2 col-span-2">
            <Label htmlFor="notes">{t('generator.form.notes')}</Label>
            <Textarea
              id="notes"
              value={data.notes}
              onChange={(e) => onChange('notes', e.target.value)}
              placeholder={t('generator.form.notesPlaceholder')}
              rows={3}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};
